<!-- No scrim, no panel, no list underneath. The review is the screen: see the
     class comment in review-mode.ts for what this replaced and why. -->
<section class="review" *ngIf="!loading; else loadingState">
  <p class="review-error" *ngIf="error">{{ error }}</p>

  <ng-container *ngIf="!error">
    <!-- The rail is the only thing that persists between items, so it carries
         everything that is true across the whole loop: where you are, how much
         is left, and what you have claimed. It sticks because a progress bar
         you have to scroll to is a progress bar nobody reads. -->
    <header class="review-rail">
      <div class="review-rail__top">
        <div class="review-rail__where">
          <span class="review-count" *ngIf="queue.length">
            <span class="review-count__n">{{ progress.position }}</span>
            <span class="review-count__of">of {{ progress.total }}</span>
          </span>
          <span class="review-scope" *ngIf="current as entry">{{ entry.packageTitle }}</span>
        </div>

        <div class="review-rail__totals">
          <!-- The number that makes clearing a queue read as getting something
               rather than emptying something. Same `gap` the strip's header
               counts, so the two are the same currency. -->
          <span class="review-claimed" *ngIf="hasClaimed">
            <span class="review-claimed__n">{{ claimed | number: '1.0-0' }}</span>
            <span class="review-claimed__k">gap claimed</span>
          </span>
          <span class="review-tally" *ngIf="outcome.decided">{{ outcome.decided }} decided</span>
          <button type="button" class="btn btn--sm" (click)="exit()">Close</button>
        </div>
      </div>

      <div
        class="review-bar"
        role="progressbar"
        [attr.aria-valuenow]="progress.position"
        [attr.aria-valuemin]="0"
        [attr.aria-valuemax]="progress.total"
        [attr.aria-label]="'Reviewed ' + progress.position + ' of ' + progress.total + ' pages'"
      >
        <span class="review-bar__fill" [style.width.%]="progress.percent"></span>
      </div>
    </header>

    <!-- ── One item ─────────────────────────────────────────────────────── -->
    <article class="review-item" *ngIf="current as entry">
      <header class="review-item__head">
        <div class="review-item__id">
          <h1 class="review-item__path">{{ entry.path }}</h1>
          <a
            class="review-item__url"
            [href]="entry.url"
            target="_blank"
            rel="noopener noreferrer"
            >{{ entry.url }}</a
          >
        </div>

        <div class="review-item__facts">
          <span class="review-fact" *ngIf="entry.keyword">
            <span class="review-fact__k">keyword</span>
            <span class="review-fact__v">{{ entry.keyword }}</span>
          </span>
          <span class="review-fact" *ngIf="entry.periodChange as change">
            <span class="review-fact__k">{{ change.metricLabel }}</span>
            <span
              class="review-fact__v"
              [class.change-down]="change.direction === 'decay'"
              [class.change-up]="change.direction === 'win'"
              >{{ change.display }}</span
            >
          </span>
          <span class="review-fact" *ngIf="entry.gap !== null">
            <span class="review-fact__k">gap</span>
            <span class="review-fact__v">{{ entry.gap | number: '1.0-0' }}</span>
          </span>
        </div>
      </header>

      <!-- Two states that look alike and must not read alike: a page you sent
           to work items and a page you put aside are both "done", and the only
           way to tell them apart afterwards is the wording here. -->
      <p class="review-state review-state--claimed" *ngIf="claimedUrlIds.has(entry.urlId)">
        Sent to work items. Keep going, or press <kbd>u</kbd> to take it back.
      </p>
      <p class="review-state review-state--dismissed" *ngIf="dismissedUrlIds.has(entry.urlId)">
        Put aside for this period. Press <kbd>u</kbd> to bring it back.
      </p>
      <!-- §0bf. A page under "Recover lost traffic" that gained says so here, in
           the neutral note style: it is context, not a state the operator set. -->
      <p class="review-state review-state--also" *ngIf="entry.standing">{{ entry.standing }}</p>
      <p class="review-state review-state--also" *ngIf="entry.alsoInPackages">
        Also listed in {{ entry.alsoInPackages }}
        {{ entry.alsoInPackages === 1 ? 'other package' : 'other packages' }} — you review it once.
      </p>

      <!-- One spinner for the page, rather than the page assembling itself in
           front of the reader. Delayed like everything else here, so a warm
           page goes straight to content. -->
      <div class="review-loading review-loading--inline" *ngIf="showLoading">
        <span class="loading-spinner" aria-hidden="true"></span>
        <p>Comparing this page against the rivals ranking for it…</p>
        <!-- After 15s the wait is a queue, not a glitch, and says so. One line,
             same spinner — see `SLOW_AFTER_MS`. -->
        <p class="review-loading__slow" *ngIf="slowLoadingMessage">{{ slowLoadingMessage }}</p>
      </div>

      <!-- The same `app-url-detail` the drawer ran, in the same mode. The
           screen around it changed; what an operator reads did not. -->
      <div class="review-item__body" [class.review-item__body--hidden]="!showContent">
        <!-- `embedReviewSurface` is what makes this a review surface rather than a
             drawer that happens to be full-width: no claim renders while a
             slower source could still overrule it. Inventory deliberately does
             not set it. -->
        <app-url-detail
          [embedUrl]="entry.url"
          embedPreset="planV2"
          [embedPlanV2InDrawer]="true"
          [embedReviewSurface]="true"
          [embedActionsOnly]="issuesOnly"
          [embedPlanV2Domain]="domain"
          (embedSettledChange)="onCurrentSettledChange($event)"
        ></app-url-detail>
      </div>
    </article>

    <!-- ── The next page, loading out of sight ──────────────────────────────
         Both snapshots are ready in milliseconds; what costs ~5s is one live
         marked-competitor evaluation the build does not yet cover. Spending it
         while the operator reads the page before means only page 1 ever pays.

         `aria-hidden` and `inert` because this is not content — it is a warm
         cache with a DOM. Without `inert` its links and buttons would be in the
         tab order, and the operator would tab from the visible card straight
         into a page they cannot see. -->
    <div class="review-prefetch" aria-hidden="true" inert *ngIf="prefetch as next">
      <app-url-detail
        [embedUrl]="next.url"
        embedPreset="planV2"
        [embedPlanV2InDrawer]="true"
        [embedReviewSurface]="true"
        [embedActionsOnly]="issuesOnly"
        [embedPlanV2Domain]="domain"
      ></app-url-detail>
    </div>

    <!-- ── The end of the belt ──────────────────────────────────────────── -->
    <div class="review-done" *ngIf="finished">
      <p class="review-done__mark" aria-hidden="true">✓</p>
      <h1 class="review-done__title">
        {{ queue.length ? 'Queue clear.' : 'Nothing to review.' }}
      </h1>
      <p class="review-done__sub" *ngIf="queue.length">
        {{ outcome.seen }} of {{ queue.length }} pages seen ·
        {{ outcome.claimed }} sent to work items · {{ outcome.dismissed }} put aside
      </p>
      <p class="review-done__sub" *ngIf="!queue.length">
        This period’s brief has no pages waiting on a decision.
      </p>
      <p class="review-done__claimed" *ngIf="hasClaimed">
        <span class="review-done__n">{{ claimed | number: '1.0-0' }}</span> uncaptured demand now has work
        against it.
      </p>
      <div class="review-done__actions">
        <button type="button" class="btn" (click)="exit()">Back to dashboard</button>
        <button type="button" class="btn btn--quiet" *ngIf="queue.length" (click)="restart()">
          Go through it again
        </button>
      </div>
    </div>

    <!-- ── The action bar ───────────────────────────────────────────────── -->
    <!-- Fixed to the bottom, not to the end of the content. The review below is
         a screen and a half on a live page, and a decision bar that scrolls off
         means scrolling back to answer — the exact motion this replaced. -->
    <footer class="review-actions" *ngIf="!finished && current">
      <div class="review-actions__nav">
        <button
          type="button"
          class="btn btn--sm btn--quiet"
          [disabled]="index === 0"
          (click)="previous()"
        >
          ← Back
        </button>
        <!-- The escape hatch. Everything this surface strips is still on URL
             Detail, and this is what makes that true rather than a claim. -->
        <button type="button" class="btn btn--sm btn--quiet" (click)="openInInventory()">
          Open in Inventory <kbd>o</kbd>
        </button>
        <button type="button" class="btn btn--sm btn--quiet" (click)="toggleIssuesOnly()">
          {{ issuesOnly ? 'Issues only' : 'All checks' }}
        </button>
      </div>

      <!-- The undo lives in the bar rather than in a toast: a toast that expires
           takes the affordance with it exactly when a slow reader wants it, and
           the notification stack is already carrying failures. It covers BOTH
           decisions — the label says which, because "Undo" over two different
           things the operator might have just done is a coin toss. -->
      <div class="review-actions__undo" *ngIf="undoable as pending">
        <span class="review-undo__text">{{ undoLabel(pending) }}</span>
        <button type="button" class="btn btn--sm" (click)="undo()">Undo</button>
      </div>

      <div class="review-actions__decide">
        <span class="review-hint">
          <kbd>s</kbd> send · <kbd>j</kbd> next · <kbd>k</kbd> back · <kbd>e</kbd> put aside ·
          <kbd>u</kbd> undo · <kbd>o</kbd> inventory · <kbd>esc</kbd> close
        </span>
        <button
          type="button"
          class="btn btn--sm btn--quiet"
          [disabled]="busy"
          (click)="dismiss()"
        >
          Put aside
        </button>
        <!-- The page's one decision. It names what it will send rather than
             what is on the page, because that is the number being agreed to. -->
        <button
          type="button"
          class="btn btn--sm btn--accent"
          *ngIf="hasSendableItems || sendBlocked"
          [disabled]="busy || sendBlocked"
          (click)="send()"
        >
          {{ sendLabel }}
        </button>
        <!-- A page with nothing registered still has to be leavable. -->
        <button
          type="button"
          class="btn btn--sm btn--accent"
          *ngIf="!hasSendableItems && !sendBlocked"
          (click)="next()"
        >
          Next →
        </button>
      </div>
    </footer>
  </ng-container>
</section>

<ng-template #loadingState>
  <div class="review-loading">
    <span class="loading-spinner" aria-hidden="true"></span>
    <p>Building this period’s review queue…</p>
  </div>
</ng-template>
